import React from "react";
import {
  YStack,
  XStack,
  SizableText,
  H3,
  H4,
  Card,
  Separator,
  Button,
  Spinner,
} from "tamagui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MaterialCommunityIcons, MaterialIcons } from "@expo/vector-icons";
import * as Animatable from 'react-native-animatable';
import { Linking, View, Dimensions, Platform } from "react-native";
import ErrorBoundary from "@utils/ErrorBoundary";
import { themeSelector } from "@reduxLocal/selectors/themeSelector";
import apis from "@api/api";
import { useAuthRequest, ResponseType } from 'expo-auth-session';
import { createURL } from 'expo-linking';
import { connectPlatformCallback } from "@api/api";
import { useDispatch } from "react-redux";
import { ADD_INTEGRATION, HIDE_LOADING_MODAL, SHOW_AUTH_MODAL, SHOW_LOADING_MODAL } from "@reduxLocal/actions/types";

interface YoutubeOptionsProps {
  integration?: {
    meta: string;
    title: string;
    endPoint?: string;
    clientId?: string;
    clientSecret?: string;
    clientKey?: string;
    scopes?: string[];
  };
  onReconnect?: () => void;
}

const YoutubeOptions = ({ integration, onReconnect }: YoutubeOptionsProps) => {
  const theme = themeSelector();
  const screenWidth = Dimensions.get('window').width;
  const isTablet = screenWidth > 768;
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  // Setup OAuth for reconnection if integration data is provided
  const discovery = integration?.endPoint ? { authorizationEndpoint: integration.endPoint } : null;
  const [request, response, promptAsync] = useAuthRequest(
    integration?.endPoint ? {
      clientId: integration.clientId || '',
      scopes: integration.scopes || [],
      redirectUri: Platform.select({ web: createURL('/universal/integrations'), default: `https://api.smartsaas.pro/app-redirect` }),
      responseType: ResponseType.Code,
      extraParams: {
        client_key: integration.clientKey || integration.clientId || '',
      }
    } : null,
    discovery || null
  );

  const handleGotCode = async (code: string, verifier?: string) => {
    if (!integration) return;
    try {
      dispatch({ type: SHOW_LOADING_MODAL });
      const res = await connectPlatformCallback(code, integration.meta, request?.codeVerifier || '', verifier);
      dispatch({ type: ADD_INTEGRATION, payload: { authType: integration.meta, data: res.data } });
      dispatch({ type: HIDE_LOADING_MODAL });
      dispatch({ type: SHOW_AUTH_MODAL, data: { content: res.message, type: 'success' } });

      // Refresh integration data and YouTube profile
      queryClient.invalidateQueries({ queryKey: ['integrationData'] });
      queryClient.invalidateQueries({ queryKey: ['youtubeProfile'] });
      queryClient.refetchQueries({ queryKey: ['youtubeProfile'] });
    } catch (error: any) {
      console.error('YouTube reconnection error:', error);
      dispatch({ type: HIDE_LOADING_MODAL });
      dispatch({ type: SHOW_AUTH_MODAL, data: { content: `Failed to reconnect ${integration.title}: ${error.message}`, type: 'error' } });
    }
  };

  React.useEffect(() => {
    if (response?.type === 'success' && integration) {
      const { code } = response.params;
      // Pass redirectUri to ensure it matches what was used in authorization request
      handleGotCode(code, request?.redirectUri);
    }
  }, [response]);

  const handleReconnect = async () => {
    if (onReconnect) {
      onReconnect();
    } else if (integration?.endPoint && promptAsync) {
      try {
        await promptAsync({ windowFeatures: { dependant: true, name: 'auth' } });
      } catch (e: any) {
        dispatch({ type: SHOW_AUTH_MODAL, data: { content: `Failed to start reconnection: ${e}`, type: "error" } });
      }
    } else {
      // Fallback: just retry the query
      refetch();
    }
  };

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["youtubeProfile"],
    queryFn: async () => {
      console.log('🎬 Fetching YouTube profile...');
      try {
        const result = await apis.getYoutubeProfile();
        console.log('✅ YouTube profile fetched:', result);
        return result;
      } catch (err: any) {
        console.error('❌ YouTube profile fetch error:', err);
        throw err;
      }
    },
    staleTime: 1000 * 60 * 5,
    retry: 2,
  });

  const channel = data?.channel;
  
  // Check if error is authentication-related
  const isAuthError = error && (
    (error as any)?.message?.includes('re-authenticate') ||
    (error as any)?.message?.includes('authentication failed') ||
    (error as any)?.message?.includes('token') ||
    (error as any)?.response?.status === 401 ||
    (error as any)?.response?.status === 403
  );

  return (
    <ErrorBoundary>
      <YStack gap="$4">
        {/* Error State */}
        {error && (
          <Animatable.View animation="fadeInUp" duration={600}>
            <Card
              backgroundColor={theme ? "#fef2f2" : "#450a0a"}
              borderColor={theme ? "#fecaca" : "#7f1d1d"}
              borderRadius={16}
              padding="$5"
              elevate
            >
              <YStack alignItems="center" gap="$4">
                <View style={{
                  backgroundColor: theme ? "#fee2e2" : "#7f1d1d",
                  borderRadius: 50,
                  padding: 16
                }}>
                  <MaterialIcons name="error-outline" size={48} color="#ef4444" />
                </View>
                <H3 color="#ef4444" fontWeight="600" textAlign="center">
                  {isAuthError ? 'YouTube Authentication Required' : 'Failed to load YouTube profile'}
                </H3>
                <SizableText
                  size="$4"
                  color={theme ? "#64748b" : "#94a3b8"}
                  textAlign="center"
                >
                  {isAuthError 
                    ? 'Your YouTube access token has expired. Please reconnect your YouTube account to continue.'
                    : 'Please try reconnecting your YouTube account, or try removing and re-adding the integration.'}
                </SizableText>
                <YStack gap="$3" width="100%">
                  {isAuthError && integration?.endPoint ? (
                    <Button
                      backgroundColor="#FF0000"
                      color="white"
                      onPress={handleReconnect}
                      borderRadius={12}
                      paddingHorizontal="$5"
                      paddingVertical="$3"
                    >
                      <XStack alignItems="center" gap="$2" justifyContent="center">
                        <MaterialIcons name="lock" size={20} color="white" />
                        <SizableText color="white" fontWeight="600">
                          Re-authenticate YouTube
                        </SizableText>
                      </XStack>
                    </Button>
                  ) : (
                    <Button
                      backgroundColor="#ef4444"
                      color="white"
                      onPress={() => refetch()}
                      borderRadius={12}
                      paddingHorizontal="$5"
                      paddingVertical="$3"
                    >
                      <XStack alignItems="center" gap="$2" justifyContent="center">
                        <MaterialIcons name="refresh" size={20} color="white" />
                        <SizableText color="white" fontWeight="600">
                          Retry Connection
                        </SizableText>
                      </XStack>
                    </Button>
                  )}
                </YStack>
              </YStack>
            </Card>
          </Animatable.View>
        )}

        {/* Loading State */}
        {isLoading && (
          <Animatable.View animation="fadeIn" duration={600}>
            <Card
              backgroundColor={theme ? "#f8fafc" : "#1e293b"}
              borderColor={theme ? "#e2e8f0" : "#334155"}
              borderRadius={16}
              padding="$6"
              elevate
            >
              <YStack alignItems="center" gap="$4">
                <Spinner size="large" color="#FF0000" />
                <H4 color={theme ? "#1e293b" : "#f1f5f9"} fontWeight="600">
                  Loading YouTube profile...
                </H4>
              </YStack>
            </Card>
          </Animatable.View>
        )}

        {/* Profile Data */}
        {channel && (
          <>
            <Animatable.View animation="fadeInUp" duration={600} delay={100}>
              <Card
                backgroundColor={theme ? "#ffffff" : "#1e293b"}
                borderColor={theme ? "#e2e8f0" : "#334155"}
                borderRadius={16}
                padding="$5"
                elevate
              >
                {/* Header with Channel Info */}
                <XStack alignItems="center" gap="$4" marginBottom="$4">
                  <View style={{
                    width: 70,
                    height: 70,
                    borderRadius: 35,
                    backgroundColor: '#FF0000',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <SizableText
                      color="white"
                      fontSize="$9"
                      fontWeight="bold"
                    >
                      {channel.snippet?.title ? channel.snippet.title.charAt(0).toUpperCase() : 'YT'}
                    </SizableText>
                  </View>

                  <YStack flex={1}>
                    <XStack alignItems="center" gap="$2">
                      <H4
                        color={theme ? "#1e293b" : "#f1f5f9"}
                        fontWeight="700"
                        numberOfLines={1}
                      >
                        {channel.snippet?.title || 'YouTube Channel'}
                      </H4>
                      <MaterialCommunityIcons name="check-circle" size={18} color="#FF0000" />
                    </XStack>
                    <SizableText
                      size="$4"
                      color={theme ? "#64748b" : "#94a3b8"}
                      numberOfLines={1}
                    >
                      {channel.statistics?.subscriberCount
                        ? `${Number(channel.statistics.subscriberCount).toLocaleString()} subscribers`
                        : `Channel ID: ${channel.id}`}
                    </SizableText>
                  </YStack>
                </XStack>

                <Separator borderColor={theme ? "#e2e8f0" : "#334155"} marginBottom="$4" />

                {/* Channel Statistics */}
                <YStack gap="$3">
                  <XStack alignItems="center" gap="$3">
                    <View style={{
                      backgroundColor: theme ? '#fef3c7' : '#78350f',
                      borderRadius: 8,
                      padding: 8
                    }}>
                      <MaterialCommunityIcons name="account-group" size={20} color={theme ? '#d97706' : '#fbbf24'} />
                    </View>
                    <YStack>
                      <SizableText
                        size="$6"
                        color={theme ? "#1e293b" : "#f1f5f9"}
                        fontWeight="700"
                      >
                        {Number(channel.statistics?.subscriberCount || 0).toLocaleString()}
                      </SizableText>
                      <SizableText
                        size="$3"
                        color={theme ? "#64748b" : "#94a3b8"}
                      >
                        Subscribers
                      </SizableText>
                    </YStack>
                  </XStack>

                  <XStack alignItems="center" gap="$3">
                    <View style={{
                      backgroundColor: theme ? '#fef3c7' : '#78350f',
                      borderRadius: 8,
                      padding: 8
                    }}>
                      <MaterialCommunityIcons name="video" size={20} color={theme ? '#d97706' : '#fbbf24'} />
                    </View>
                    <YStack>
                      <SizableText
                        size="$6"
                        color={theme ? "#1e293b" : "#f1f5f9"}
                        fontWeight="700"
                      >
                        {Number(channel.statistics?.videoCount || 0).toLocaleString()}
                      </SizableText>
                      <SizableText
                        size="$3"
                        color={theme ? "#64748b" : "#94a3b8"}
                      >
                        Videos
                      </SizableText>
                    </YStack>
                  </XStack>

                  <XStack alignItems="center" gap="$3">
                    <View style={{
                      backgroundColor: theme ? '#fef3c7' : '#78350f',
                      borderRadius: 8,
                      padding: 8
                    }}>
                      <MaterialCommunityIcons name="eye" size={20} color={theme ? '#d97706' : '#fbbf24'} />
                    </View>
                    <YStack>
                      <SizableText
                        size="$6"
                        color={theme ? "#1e293b" : "#f1f5f9"}
                        fontWeight="700"
                      >
                        {Number(channel.statistics?.viewCount || 0).toLocaleString()}
                      </SizableText>
                      <SizableText
                        size="$3"
                        color={theme ? "#64748b" : "#94a3b8"}
                      >
                        Total Views
                      </SizableText>
                    </YStack>
                  </XStack>

                  {channel.snippet?.description && (
                    <Card
                      backgroundColor={theme ? "#f8fafc" : "#334155"}
                      borderRadius={12}
                      padding="$3"
                      marginTop="$2"
                    >
                      <XStack gap="$2">
                        <MaterialCommunityIcons name="information" size={16} color={theme ? '#64748b' : '#94a3b8'} />
                        <SizableText
                          size="$3"
                          color={theme ? "#64748b" : "#94a3b8"}
                          flex={1}
                          numberOfLines={3}
                        >
                          {channel.snippet.description}
                        </SizableText>
                      </XStack>
                    </Card>
                  )}
                </YStack>

                {/* Action Buttons */}
                <YStack gap="$3" marginTop="$4">
                  <Button
                    backgroundColor="#FF0000"
                    color="white"
                    onPress={async () => {
                      const url = `https://youtube.com/channel/${channel.id}`;
                      try {
                        await Linking.openURL(url);
                      } catch (error) {
                        console.error("Error opening YouTube channel:", error);
                      }
                    }}
                    borderRadius={12}
                    paddingVertical="$3"
                  >
                    <XStack alignItems="center" gap="$2">
                      <MaterialIcons name="open-in-new" size={18} color="white" />
                      <SizableText color="white" fontWeight="600" size="$4">
                        View Channel on YouTube
                      </SizableText>
                    </XStack>
                  </Button>

                  <Button
                    backgroundColor={theme ? "#0ea5e9" : "#38bdf8"}
                    color="white"
                    onPress={async () => {
                      const studioUrl = "https://studio.youtube.com";
                      try {
                        await Linking.openURL(studioUrl);
                      } catch (error) {
                        console.error("Error opening YouTube Studio:", error);
                      }
                    }}
                    borderRadius={12}
                    paddingVertical="$3"
                  >
                    <XStack alignItems="center" gap="$2">
                      <MaterialCommunityIcons name="youtube-studio" size={20} color="white" />
                      <SizableText color="white" fontWeight="600" size="$4">
                        Open YouTube Studio
                      </SizableText>
                    </XStack>
                  </Button>
                </YStack>
              </Card>
            </Animatable.View>
          </>
        )}

        {/* Success Message */}
        {channel && (
          <Animatable.View animation="fadeInUp" duration={600} delay={200}>
            <Card
              backgroundColor={theme ? "#f0fdf4" : "#064e3b"}
              borderColor={theme ? "#dcfce7" : "#065f46"}
              borderRadius={16}
              padding="$5"
              elevate
            >
              <YStack alignItems="center" gap="$3">
                <View style={{
                  backgroundColor: theme ? "#d1fae5" : "#065f46",
                  borderRadius: 50,
                  padding: 12
                }}>
                  <MaterialIcons name="check-circle" size={32} color={theme ? "#10b981" : "#34d399"} />
                </View>
                <H4
                  color={theme ? "#059669" : "#10b981"}
                  fontWeight="600"
                  textAlign="center"
                >
                  YouTube Connected Successfully!
                </H4>
                <SizableText
                  size="$4"
                  color={theme ? "#065f46" : "#34d399"}
                  textAlign="center"
                >
                  You can now access your videos, analytics, and YouTube Studio features directly from SmartSaaS.
                </SizableText>
              </YStack>
            </Card>
          </Animatable.View>
        )}
      </YStack>
    </ErrorBoundary>
  );
};

export default YoutubeOptions;
